<script setup lang="ts">
/**
 * Knowledge inbox, centred on the pre-triage: every open item has a verdict
 * (accept / reject / review) with a reason, items are grouped by verdict ×
 * kind × category × source × origin, and admins decide whole groups after a
 * preview (a token for exactly the previewed items; with the type, support
 * and title filters the cards were counted with) — or open a group and
 * decide items one by one. People with knowledge.read see the items and
 * their verdicts. Server: src/knowledge/inbox.ts, triage.ts, triage/groups.ts.
 */
import { Check, Inbox, LayoutList, Layers, Loader2, X } from "@lucide/vue";
import { useQueryClient } from "@tanstack/vue-query";
import { refDebounced } from "@vueuse/core";
import { computed, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { toast } from "vue-sonner";
import { TERMINAL_JOB, useJob } from "@/api/jobs";
import { useInboxSummary } from "@/api/knowledge";
import EmptyState from "@/components/common/EmptyState.vue";
import ErrorState from "@/components/common/ErrorState.vue";
import FilterChips from "@/components/common/FilterChips.vue";
import ListSkeleton from "@/components/common/ListSkeleton.vue";
import PageHeader from "@/components/common/PageHeader.vue";
import PageTabs, { type PageTabItem } from "@/components/common/PageTabs.vue";
import SimpleSelect from "@/components/common/SimpleSelect.vue";
import InboxGroupCard from "@/components/knowledge/InboxGroupCard.vue";
import InboxGroupDialog from "@/components/knowledge/InboxGroupDialog.vue";
import InboxItemList from "@/components/knowledge/InboxItemList.vue";
import InboxTriagePanel from "@/components/knowledge/InboxTriagePanel.vue";
import {
  applyGroupDecision, groupApplyOutcome, MAX_GROUPS, planGroupDecision, plural, useGroupSelection, useInboxGroups, useTriageSummary, VERDICTS,
  type GroupApplyOutcome, type GroupPlan, type GroupVerdict, type TriageInboxFilter,
} from "@/components/knowledge/inboxTriage";
import KnowledgeNav from "@/components/knowledge/KnowledgeNav.vue";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { useSession } from "@/composables/useSession";
import { t } from "@/copy/strings";
import { errorMessage } from "@/lib/errors";
import { formatNumber } from "@/lib/format";

definePage({ meta: { title: "Knowledge inbox", requires: { capability: "knowledge.read" } } });

const queryClient = useQueryClient();
const { can } = useSession();
const canDecide = computed(() => can("knowledge.admin"));

const KIND_LABEL = { proposal: "Suggestions", conflict: "Conflicts", duplicate: "Duplicates", feedback: "Feedback" } as const;
type Kind = keyof typeof KIND_LABEL;
const VERDICT_KEYS = Object.keys(VERDICTS) as GroupVerdict[];

// --- Filters (deep links: /knowledge/inbox?kind=conflict, ?verdict=reject, ?view=items) ----------
const query = useRoute().query;
const one = (v: unknown) => (typeof v === "string" ? v : undefined);
const kind = ref<Kind | undefined>(one(query.kind) && one(query.kind)! in KIND_LABEL ? (one(query.kind) as Kind) : undefined);
const verdict = ref<GroupVerdict | undefined>(VERDICT_KEYS.includes(one(query.verdict) as GroupVerdict) ? (one(query.verdict) as GroupVerdict) : undefined);
const viewChoice = ref<"groups" | "items">(one(query.view) === "items" ? "items" : "groups");
/** Groups are for admins (they decide); feedback is never grouped. */
const view = computed(() => (canDecide.value && kind.value !== "feedback" ? viewChoice.value : "items"));
const viewTabs = computed<PageTabItem[]>(() => [
  { id: "groups", label: "Groups", icon: Layers, disabled: kind.value === "feedback" },
  { id: "items", label: "Items", icon: LayoutList },
]);
const viewModel = computed({ get: () => view.value, set: (v: string) => (viewChoice.value = v === "items" ? "items" : "groups") });
const sourceId = ref<string | undefined>();
const entityType = ref<string | undefined>();
const support = ref<"one" | "two" | "four" | undefined>();
const search = ref("");
const q = refDebounced(search, 300);
const SUPPORT: Record<NonNullable<typeof support.value>, Pick<TriageInboxFilter, "minSupport" | "maxSupport">> = {
  one: { maxSupport: 1 },
  two: { minSupport: 2, maxSupport: 3 },
  four: { minSupport: 4 },
};
const filter = computed<TriageInboxFilter>(() => ({
  kind: kind.value,
  verdict: verdict.value,
  sourceId: sourceId.value,
  entityType: entityType.value,
  ...(support.value ? SUPPORT[support.value] : {}),
  q: q.value.trim() || undefined,
}));
const filtered = computed(() => Boolean(kind.value || verdict.value || sourceId.value || entityType.value || support.value || q.value.trim()));

function clearFilters() {
  kind.value = verdict.value = sourceId.value = entityType.value = support.value = undefined;
  search.value = "";
}

const summary = useInboxSummary(filter);
const triage = useTriageSummary(canDecide);
const groups = useInboxGroups(filter, () => view.value === "groups");
const groupList = computed(() => groups.data.value?.groups ?? []);
const total = computed(() => summary.data.value?.total ?? 0);
const rejectGroups = computed(() => groupList.value.filter((g) => g.verdict === "reject"));
const rejectItems = computed(() => rejectGroups.value.reduce((n, g) => n + g.count, 0));

const kindOptions = computed(() =>
  (summary.data.value?.byKind ?? [])
    .filter((k) => k.kind in KIND_LABEL && (view.value === "items" || k.kind !== "feedback"))
    .map((k) => ({ value: k.kind as Kind, label: KIND_LABEL[k.kind as Kind], count: k.count })),
);
const verdictOptions = computed(() =>
  (summary.data.value?.byVerdict ?? []).filter((v) => v.verdict in VERDICTS).map((v) => ({ value: v.verdict as GroupVerdict, label: VERDICTS[v.verdict as GroupVerdict].label, count: v.count })),
);
const sourceOptions = computed(() => (summary.data.value?.bySource ?? []).map((s) => ({ value: s.id, label: `${s.name} (${formatNumber(s.count)})` })));
const typeOptions = computed(() => (summary.data.value?.byType ?? []).map((s) => ({ value: s.type, label: `${s.type} (${formatNumber(s.count)})` })));
const supportOptions = computed(() => {
  const b = summary.data.value?.bySupport;
  return [
    { value: "one" as const, label: "1 page", count: b?.one ?? 0 },
    { value: "two" as const, label: "2–3 pages", count: b?.twoToThree ?? 0 },
    { value: "four" as const, label: "4+ pages", count: b?.fourPlus ?? 0 },
  ].filter((o) => o.count > 0 || support.value === o.value);
});

// --- Group selection (cleared when the filters change; groups decided meanwhile drop out) ------------
const { selected, allSelected, items: selectedItems, toggleAll, set, clear: clearSelection } = useGroupSelection(groupList, filter);
function setSelected(key: string, on: boolean) {
  if (!set(key, on)) toast.info(`Up to ${MAX_GROUPS} groups at a time.`);
}
const labels = computed(() => new Map(groupList.value.map((g) => [g.key, g.label])));
/** What each card counted: the preview warns when a plan differs. */
const shownCounts = computed(() => new Map(groupList.value.map((g) => [g.key, g.count])));

// --- Preview and apply -----------------------------------------------------------------------------
const plan = ref<GroupPlan | null>(null);
const planOpen = ref(false);
const planning = ref(false);
const applying = ref(false);
const applyJobId = ref<string | null>(null);
const applyLabel = ref("");
const applyJob = useJob(applyJobId);
const applyProgress = computed(() => Math.round((applyJob.data.value?.progress ?? 0) * 100));

async function preview(keys: string[], decision: "accept" | "reject") {
  if (!keys.length) return;
  planning.value = true;
  try {
    plan.value = await planGroupDecision(keys, decision, filter.value);
    planOpen.value = true;
  } catch (err) {
    toast.error(errorMessage(err));
  } finally {
    planning.value = false;
  }
}

async function apply(options: { reason: string; remember: boolean }) {
  if (!plan.value?.token) return;
  applying.value = true;
  try {
    const r = await applyGroupDecision(plan.value.token, options);
    applyJobId.value = r.jobId;
    applyLabel.value = `${r.decision === "accept" ? "Accepting" : "Rejecting"} ${plural(r.items, ["item", "items"])}`;
    planOpen.value = false;
    plan.value = null;
    clearSelection();
  } catch (err) {
    toast.error(errorMessage(err));
  } finally {
    applying.value = false;
  }
}

watch(
  () => applyJob.data.value?.status,
  async (status) => {
    if (!status || !TERMINAL_JOB.includes(status as never)) return;
    const outcome = groupApplyOutcome(status, (applyJob.data.value?.result ?? null) as GroupApplyOutcome | null, applyJob.data.value?.statusMessage);
    toast[outcome.level](outcome.message, { description: outcome.description });
    applyJobId.value = null;
    await queryClient.invalidateQueries({ queryKey: ["knowledge"] });
  },
);
const busy = computed(() => Boolean(applyJobId.value));
</script>

<template>
  <div>
    <PageHeader :title="t.knowledge.inbox" :description="t.knowledge.inboxDescription">
      <template #badges>
        <Badge v-if="summary.data.value && !filtered" variant="muted" class="tabular-nums">{{ formatNumber(total) }} open</Badge>
      </template>
    </PageHeader>
    <KnowledgeNav />

    <InboxTriagePanel v-if="canDecide" v-model:verdict="verdict" :summary="triage.data.value" :loading="triage.isPending.value" />

    <!-- Applying a group decision -->
    <div v-if="busy" class="surface-inset mb-4 flex flex-col gap-2 px-4 py-3 text-sm" role="status">
      <div class="flex items-center gap-2">
        <Loader2 class="size-4 animate-spin" aria-hidden="true" />
        <span class="font-medium">{{ applyLabel }}…</span>
        <span class="min-w-0 truncate text-muted-foreground">{{ applyJob.data.value?.statusMessage }}</span>
        <span class="ml-auto tabular-nums text-muted-foreground">{{ applyProgress }}%</span>
      </div>
      <div class="h-1.5 w-full overflow-hidden rounded-full bg-primary/15"><div class="h-full bg-primary transition-[width]" :style="{ width: `${applyProgress}%` }" /></div>
    </div>

    <!-- View and filters -->
    <div class="mb-4 flex flex-col gap-3">
      <div class="flex flex-col gap-3 sm:flex-row sm:items-center">
        <PageTabs v-if="canDecide" v-model="viewModel" :items="viewTabs" variant="segmented" label="View" id-prefix="inbox-view" class="shrink-0 self-start" />
        <FilterChips v-if="kindOptions.length > 1 || kind" v-model="kind" :options="kindOptions" label="Kind" />
      </div>
      <FilterChips v-if="!canDecide && (verdictOptions.length > 1 || verdict)" v-model="verdict" :options="verdictOptions" label="Verdict" />
      <div class="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        <SimpleSelect v-model="sourceId" :options="sourceOptions" allow-empty empty-label="All sources" aria-label="Source" />
        <SimpleSelect v-model="entityType" :options="typeOptions" allow-empty empty-label="All types" aria-label="Entity type" />
        <Input v-model="search" type="search" placeholder="Search titles…" aria-label="Search titles" class="lg:col-span-2" />
      </div>
      <FilterChips v-if="supportOptions.length > 1 || support" v-model="support" :options="supportOptions" label="Supported by" />
      <p class="flex flex-wrap items-center gap-x-2 text-sm text-muted-foreground">
        <span>
          <span class="font-medium text-foreground tabular-nums">{{ formatNumber(total) }}</span> {{ total === 1 ? "item" : "items" }}
          <template v-if="view === 'groups' && groups.data.value"> in {{ plural(groupList.length, ["group", "groups"]) }}</template>
          <template v-if="verdict"> · verdict {{ VERDICTS[verdict].label.toLowerCase() }}</template>
        </span>
        <button v-if="filtered" type="button" class="cursor-pointer text-primary hover:underline" @click="clearFilters">Clear filters</button>
      </p>
    </div>

    <!-- Groups -->
    <template v-if="view === 'groups'">
      <ListSkeleton v-if="groups.isPending.value" :rows="4" />
      <ErrorState v-else-if="groups.error.value" :error="groups.error.value" @retry="groups.refetch()" />
      <EmptyState
        v-else-if="!groupList.length"
        :icon="Inbox"
        :title="filtered ? 'No groups match these filters' : t.knowledge.inboxEmpty"
        :description="filtered ? 'Try another verdict, source or search.' : 'New suggestions from connectors and AI extraction land here, get a verdict, and wait for a decision.'"
      />
      <template v-else>
        <!-- The fastest way through a backlog: the noise first, together. -->
        <div v-if="canDecide && !verdict && rejectGroups.length" class="mb-4 flex flex-col gap-2 rounded-lg border border-destructive/25 bg-destructive-soft/60 px-4 py-3 text-sm sm:flex-row sm:items-center">
          <p class="min-w-0 flex-1">
            <span class="font-medium">Start with the noise:</span> {{ plural(rejectItems, ["item", "items"]) }} in {{ plural(rejectGroups.length, ["group", "groups"]) }} are triaged as reject. Check their examples, then reject them together.
          </p>
          <Button size="sm" variant="outline" class="self-start sm:self-center" @click="verdict = 'reject'">Show reject groups</Button>
        </div>
        <p v-else-if="canDecide && verdict === 'reject' && groupList.length > 1" class="mb-3 text-sm text-muted-foreground">
          Read each group's examples, select the ones that are noise and reject them in one go: the preview shows exactly what changes.
        </p>
        <div class="mb-3 flex items-center gap-2 px-1 text-sm">
          <label class="flex cursor-pointer items-center gap-2">
            <Checkbox :model-value="allSelected" :disabled="busy || groups.isPlaceholderData.value" aria-label="Select all groups shown" @update:model-value="(v) => toggleAll(v === true)" />
            {{ groupList.length > MAX_GROUPS ? `Select the first ${MAX_GROUPS} groups` : "Select all groups shown" }}
          </label>
        </div>
        <ul class="flex flex-col gap-3">
          <li v-for="g in groupList" :key="g.key">
            <InboxGroupCard
              :group="g"
              :can-decide="canDecide"
              :disabled="busy || groups.isPlaceholderData.value"
              :planning="planning"
              :selected="selected.has(g.key)"
              :filter="filter"
              @update:selected="(on) => setSelected(g.key, on)"
              @decide="(d) => preview([g.key], d)"
            />
          </li>
        </ul>
        <p v-if="(groups.data.value?.groups.length ?? 0) >= 200" class="mt-3 text-center text-xs text-muted-foreground">The 200 largest groups are shown: narrow the filters to see the rest.</p>
      </template>

      <!-- Decide the selected groups -->
      <div
        v-if="selected.size"
        class="sticky bottom-[calc(var(--tabbar-offset,0px)+1rem)] z-10 mt-4 flex flex-col gap-2 rounded-xl border bg-card/95 px-4 py-3 shadow-lg backdrop-blur sm:flex-row sm:items-center"
        role="region"
        aria-label="Selected groups"
      >
        <p class="text-sm">
          <span class="font-medium">{{ plural(selected.size, ["group", "groups"]) }}</span> ·
          <span class="tabular-nums">{{ plural(selectedItems, ["item", "items"]) }}</span>
        </p>
        <div class="flex flex-wrap gap-2 sm:ml-auto">
          <Button size="sm" variant="ghost" @click="clearSelection()">Clear</Button>
          <Button size="sm" variant="outline" :disabled="planning || busy" @click="preview([...selected], 'accept')"><Check /> Accept…</Button>
          <Button size="sm" variant="destructive" :disabled="planning || busy" @click="preview([...selected], 'reject')">
            <Loader2 v-if="planning" class="animate-spin" aria-hidden="true" /><X v-else /> Reject…
          </Button>
        </div>
      </div>
    </template>

    <!-- Items -->
    <template v-else>
      <p v-if="canDecide && kind !== 'feedback'" class="mb-3 text-sm text-muted-foreground">
        Deciding many at once? <button type="button" class="cursor-pointer text-primary hover:underline" @click="viewChoice = 'groups'">Switch to groups</button>: a preview shows what changes first.
      </p>
      <InboxItemList
        :filter="filter"
        :can-decide="canDecide"
        :disabled="busy"
        :empty-title="filtered ? 'Nothing matches these filters' : t.knowledge.inboxEmpty"
        :empty-description="filtered ? 'Try another verdict, source, type or search.' : 'New suggestions from connectors and AI extraction land here for a human decision.'"
      />
    </template>

    <InboxGroupDialog v-model:open="planOpen" :plan="plan" :labels="labels" :shown="shownCounts" :applying="applying" @apply="apply" />
  </div>
</template>
